import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useRealtime } from './useRealtime';
import { Bell, Receipt, Check, AlertTriangle } from 'lucide-react';
import { playWaiterCall, playBillRequest } from './notificationSounds';

const PAYMENT_LABEL = { cash: 'Numerar', card: 'Card' };

const TYPE_META = {
    call_waiter: { label: 'Ospătar chemat', icon: Bell, variant: 'magenta' },
    bill_request: { label: 'Cerere notă', icon: Receipt, variant: 'amber' },
};

function formatWait(iso) {
    if (!iso) return '';
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return `acum ${sec}s`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `acum ${min} min`;
    return `acum ${Math.floor(min / 60)}h`;
}

export function WaiterAlertPanel({ restaurantId, userRole }) {
    const showPanel = ['waiter', 'restaurant_admin', 'manager'].includes(userRole);
    const [alerts, setAlerts] = useState([]);
    const [acking, setAcking] = useState(null);

    const reload = useCallback(() => {
        if (!showPanel) return;
        api.get('/r/alerts').then(r => setAlerts(r.data.items || [])).catch(() => {});
    }, [showPanel]);

    useEffect(() => { reload(); }, [reload]);

    const onMessage = useCallback((msg) => {
        if (!showPanel) return;
        if (msg.type === 'call_waiter' || msg.type === 'bill_request') {
            if (msg.alert) {
                setAlerts(prev => {
                    const rest = prev.filter(a => a.id !== msg.alert.id);
                    return [...rest, msg.alert].sort((a, b) => a.called_at.localeCompare(b.called_at));
                });
            } else {
                reload();
            }
            if (msg.type === 'call_waiter') playWaiterCall();
            else playBillRequest();
        } else if (msg.type === 'alert_acknowledged' && msg.alert) {
            setAlerts(prev => prev.filter(a => a.id !== msg.alert.id));
        }
    }, [showPanel, reload]);

    useRealtime(restaurantId, onMessage);

    const sorted = useMemo(
        () => [...alerts].sort((a, b) => a.called_at.localeCompare(b.called_at)),
        [alerts],
    );

    const ack = async (alertId) => {
        setAcking(alertId);
        try {
            await api.post(`/r/alerts/${alertId}/ack`);
            setAlerts(prev => prev.filter(a => a.id !== alertId));
        } catch (e) {
            console.error(e);
        } finally {
            setAcking(null);
        }
    };

    if (!showPanel || !sorted.length) return null;

    return (
        <div className="waiter-alert-panel" data-testid="waiter-alert-panel">
            <div className="waiter-alert-panel-header">
                <AlertTriangle size={16} color="var(--neon-magenta)" />
                <span>Apeluri active</span>
                <span className="badge badge-magenta">{sorted.length}</span>
            </div>
            <div className="waiter-alert-list">
                {sorted.map((alert, index) => {
                    const meta = TYPE_META[alert.type] || TYPE_META.call_waiter;
                    const Icon = meta.icon;
                    const isTop = index === 0;
                    return (
                        <div
                            key={alert.id}
                            className={`waiter-alert-card waiter-alert-${meta.variant} ${isTop ? 'waiter-alert-priority' : ''}`}
                            data-testid={`waiter-alert-${alert.id}`}
                        >
                            <div className="waiter-alert-priority-badge">#{index + 1}</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <Icon size={15} />
                                    <strong style={{ fontSize: '0.95rem' }}>{meta.label}</strong>
                                </div>
                                <div className="headline" style={{ fontSize: '1.7rem', margin: '0.1rem 0' }}>
                                    Masa {alert.table_number}
                                </div>
                                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                    Chemat {formatWait(alert.called_at)}
                                    {alert.last_called_at && alert.last_called_at !== alert.called_at && (
                                        <span> · reapela {formatWait(alert.last_called_at)}</span>
                                    )}
                                    {alert.type === 'bill_request' && alert.payment_method && (
                                        <span style={{ display: 'block', marginTop: 4, color: alert.payment_method === 'card' ? 'var(--neon-cyan)' : 'var(--amber)', fontWeight: 600 }}>
                                            Plată: {PAYMENT_LABEL[alert.payment_method] || alert.payment_method}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={acking === alert.id}
                                onClick={() => ack(alert.id)}
                                data-testid={`alert-ack-${alert.id}`}
                                style={isTop ? { background: 'var(--neon-teal)', boxShadow: '0 0 16px rgba(20,241,149,0.35)' } : undefined}
                            >
                                {acking === alert.id ? <span className="spinner" /> : <><Check size={14} /> Am venit</>}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
